import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PersonIcon from '@mui/icons-material/Person';
import { Grid, Button, IconButton, Box } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BiCalendar } from 'react-icons/bi';
import {
  ICalendarAccount,
  IOutlookCalendarAccount,
  fetchGoogleCalendarEvents,
  fetchOutlookCalendarEvents,
  deleteGoogleCalendarEvent,
  deleteOutlookCalendarEvent,
  IOutlookCalendarEvent,
} from 'wisestep-api';
import {
  WsColors,
  Item,
  WsTypography,
  WsTypoVariants,
  Spinner,
  WsDialog,
  ScrollableTabs,
  WsDialogSize,
  WsTypoColors,
  WsIconText,
  WsFilterDialog,
  WsDatePicker,
} from 'wisestep-base';

import CalendarEvent, {
  VideoConferenceOptions,
} from '../../../components/CalendarEvent/CalendarEvent';
import CalendarEventListingCard, {
  generateTimeString,
} from '../../../components/CalendarEvent/CalendarEventListingCard';
import useCopyToClipboard from '../../../features/candidates/talentpool/hooks/useCopyToClipboard';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

import DashboardGrid from './DashboardGrid';

export const CalendarEventCardEditDialog = ({
  calendarObj,
  open,
  onClose,
  onEdit,
  onDelete,
  onCopy,
}: {
  calendarObj: IOutlookCalendarEvent;
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: (eventId?: string) => void;
  onCopy: (content: string) => void;
}) => (
  <WsDialog openDialog={open} size={WsDialogSize.SMALL} onClose={onClose}>
    <Grid container p={4} rowGap={2}>
      <Grid item xs={10}>
        <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.SECONDARY} bold>
          {calendarObj?.eventSummary}
        </WsTypography>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.SECONDARY} bold>
          {`${new Date(calendarObj?.from ?? '').toLocaleString('default', {
            month: 'short',
            day: 'numeric',
          })}, ` + generateTimeString(calendarObj?.from, calendarObj?.to)}
        </WsTypography>
      </Grid>
      <Grid item xs={2} display="flex" justifyContent="end" alignItems="start">
        <IconButton>
          <EditIcon color="primary" fontSize="small" onClick={onEdit} />
        </IconButton>
        <IconButton>
          <DeleteIcon color="error" fontSize="small" onClick={() => onDelete()} />
        </IconButton>
      </Grid>
      <Grid item container xs={12}>
        <Grid item xs={12}>
          <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.SECONDARY} bold>
            Location
          </WsTypography>
        </Grid>
        <Grid
          item
          xs={10}
          p={1}
          borderRadius={2}
          border={2}
          borderColor={WsColors.BorderGrey}
          sx={{ wordWrap: 'break-word' }}
        >
          <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY} bold>
            {calendarObj?.location}
          </WsTypography>
        </Grid>
      </Grid>
      {calendarObj?.meeting && (
        <Grid item container xs={12} columnSpacing={1} alignItems="center">
          <Grid item xs={12}>
            <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.SECONDARY} bold>
              Online Conference Link
            </WsTypography>
          </Grid>
          <Grid
            item
            xs={10}
            p={1}
            borderRadius={2}
            border={2}
            borderColor={WsColors.BorderGrey}
            sx={{ wordWrap: 'break-word' }}
          >
            <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY} bold>
              {calendarObj?.meeting}
            </WsTypography>
          </Grid>
          <Grid item xs={2}>
            <IconButton onClick={() => onCopy(calendarObj?.meeting ?? '')}>
              <ContentCopyIcon color="primary" fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      )}
      <Grid item container xs={12}>
        <Grid item xs={12}>
          <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.SECONDARY} bold>
            Description
          </WsTypography>
        </Grid>
        <Grid
          item
          xs={10}
          p={1}
          borderRadius={2}
          border={2}
          borderColor={WsColors.BorderGrey}
          sx={{ wordWrap: 'break-word' }}
        >
          <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY} bold>
            {calendarObj?.eventDescription ?? ''}
          </WsTypography>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box mx={2}>
          <WsIconText
            icon={<GroupIcon color="primary" />}
            text={`${calendarObj?.attendees ? calendarObj?.attendees.length : 0} Guests`}
            textSize={WsTypoVariants.TITLE}
            textColor={WsTypoColors.PRIMARY}
          />
        </Box>
        {calendarObj?.attendees &&
          calendarObj.attendees.map((attendeeName: string | null, index) => (
            <Box my={1} key={index} mx={2}>
              <WsIconText
                icon={<PersonIcon color="primary" />}
                text={attendeeName ?? ''}
                textSize={WsTypoVariants.BODY1}
                textColor={WsTypoColors.PRIMARY}
              />
            </Box>
          ))}
      </Grid>
    </Grid>
  </WsDialog>
);

const CalendarBoard = ({
  calendarEventAccountGoogle,
  calendarEventAccountOutlook,
}: {
  calendarEventAccountGoogle: ICalendarAccount | null;
  calendarEventAccountOutlook: IOutlookCalendarAccount | null;
}) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const dayStart = new Date();
  const dayEnd = new Date();
  dayStart.setHours(0, 0, 0);
  dayEnd.setHours(23, 59, 59);
  const methods = useForm<{ eventDateTill: Date }>({
    defaultValues: {
      eventDateTill: dayStart,
    },
  });
  const { watch, setValue } = methods;
  const eventDate = watch('eventDateTill');
  const [events, setEvents] = useState<IOutlookCalendarEvent[]>([]);
  const [eventIndex, setSelectedEventIndex] = useState<number>(0);
  const [query, setQuery] = useState<{ startDate: Date; endDate: Date }>({
    startDate: dayStart,
    endDate: dayEnd,
  });
  const [loader, setLoader] = useState<boolean>(true);
  const [openEventCreation, setOpenEventCreation] = useState<boolean>(false);
  const [switchToOutlook, setSwitchToOutlook] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [, copy] = useCopyToClipboard();
  const onCopy = (content: string) => copy(content);

  useEffect(() => {
    const startDate = new Date(eventDate);
    const endDate = new Date(eventDate);
    startDate.setHours(0, 0, 0);
    endDate.setHours(23, 59, 59);
    setQuery({ startDate, endDate });
  }, [eventDate]);

  useEffect(() => {
    const callGoogleEventListing = async () => {
      try {
        setLoader(true);
        const response = calendarEventAccountGoogle
          ? await fetchGoogleCalendarEvents({
              googleOAuth: calendarEventAccountGoogle,
              from: query.startDate,
              attendees: [],
              entityId: null,
              entityType: '',
              eventDescription: '',
              eventSummary: '',
              location: '',
              reminders: [],
              to: query.endDate,
            })
          : null;
        setEvents(response?.data);
        setLoader(false);
      } catch (e) {
        setLoader(false);
      }
    };
    if (!switchToOutlook && calendarEventAccountGoogle && !openEventCreation)
      callGoogleEventListing();
  }, [calendarEventAccountGoogle, query, openEventCreation, switchToOutlook]);

  useEffect(() => {
    const callOutlookListing = async () => {
      try {
        setLoader(true);
        const response = calendarEventAccountOutlook
          ? await fetchOutlookCalendarEvents({
              outlookOAuthToken: calendarEventAccountOutlook,
              from: query.startDate,
              attendees: [],
              entityId: null,
              entityType: '',
              eventDescription: '',
              eventSummary: '',
              location: '',
              reminders: [],
              to: query.endDate,
            })
          : null;
        setEvents(response?.data);
        setLoader(false);
      } catch (e) {
        setLoader(false);
      }
    };
    if (switchToOutlook && calendarEventAccountOutlook && !openEventCreation) callOutlookListing();
  }, [query, openEventCreation, calendarEventAccountOutlook, switchToOutlook]);

  const addEvent = () => setOpenEventCreation(true);

  const deleteEvent = async (eventInfo?: { eventId: string; selectedMeeting: string }) => {
    const defaultReq = {
      from: new Date(),
      attendees: [],
      entityId: null,
      entityType: '',
      eventDescription: '',
      eventSummary: '',
      eventId: eventInfo?.eventId ?? events[eventIndex]?.eventId,
      location: '',
      reminders: [],
      to: new Date(),
    };
    if (isEmpty(eventInfo)) {
      try {
        if (!switchToOutlook)
          await deleteGoogleCalendarEvent({
            googleOAuth: calendarEventAccountGoogle,
            ...defaultReq,
          });
        else if (switchToOutlook)
          await deleteOutlookCalendarEvent({
            outlookOAuthToken: calendarEventAccountOutlook,
            ...defaultReq,
          });
        setNotification(success('Event Deleted Successfully!'));
        setQuery((prev) => ({ ...prev }));
        setOpenEdit(false);
        setOpenEventCreation(false);
      } catch (e) {
        setNotification(failed('Some error occurred. Please try later'));
      }
    } else {
      try {
        if (eventInfo?.selectedMeeting === VideoConferenceOptions.GOOGLE_MEET)
          await deleteGoogleCalendarEvent({
            googleOAuth: calendarEventAccountGoogle,
            ...defaultReq,
          });
        else if (eventInfo?.selectedMeeting === VideoConferenceOptions.TEAMS)
          await deleteOutlookCalendarEvent({
            outlookOAuthToken: calendarEventAccountOutlook,
            ...defaultReq,
          });
        setNotification(success('Event Deleted Successfully!'));
        setQuery((prev) => ({ ...prev }));
        setOpenEdit(false);
      } catch (e) {
        setNotification(failed('Some error occurred. Please try later'));
      }
    }
  };

  const modifyDateQuery = {
    toPrevious: () => {
      const prevDate = new Date(eventDate);
      prevDate.setDate(prevDate.getDate() - 1);
      setValue('eventDateTill', prevDate);
    },
    toNext: () => {
      const nextDate = new Date(eventDate);
      nextDate.setDate(nextDate.getDate() + 1);
      setValue('eventDateTill', nextDate);
    },
  };

  return (
    <DashboardGrid container item xs={12} bgcolor={WsColors.White} sx={{ height: 600 }}>
      <Grid item md={4} sm={3}>
        <Item>
          <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
            Calendar schedule
          </WsTypography>
        </Item>
      </Grid>
      <Grid item container md={4} sm={3} pt={2.5}>
        <FormProvider {...methods}>
          <Grid item xs={2}>
            <IconButton onClick={modifyDateQuery.toPrevious}>
              <KeyboardArrowLeftIcon color="primary" />
            </IconButton>
          </Grid>
          <Grid item xs={8}>
            <WsFilterDialog
              label="Fetch events for:"
              name={eventDate.toLocaleString('default', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
              icon={<BiCalendar size={26} color={WsColors.TheBlues500} />}
            >
              <Grid item m={2} minWidth={200}>
                <WsDatePicker label="Pick your day" name={'eventDateTill'} size="small" />
              </Grid>
            </WsFilterDialog>
          </Grid>
          <Grid item xs={2}>
            <IconButton onClick={modifyDateQuery.toNext}>
              <KeyboardArrowRightIcon color="primary" />
            </IconButton>
          </Grid>
        </FormProvider>
      </Grid>
      <Grid item md={4} sm={5}>
        <Item>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addEvent}>
            Add Calendar Event
          </Button>
        </Item>
      </Grid>
      <Grid item xs={12}>
        <ScrollableTabs
          scrollList={[
            {
              tabName: 'Google',
              tabCount: !switchToOutlook ? events.length : 0,
            },
            {
              tabName: 'Microsoft',
              tabCount: switchToOutlook ? events.length : 0,
            },
          ]}
          getTabIndex={(tabNumber) => {
            if (tabNumber === 1) {
              setEvents([]);
              setSwitchToOutlook(true);
            } else {
              setEvents([]);
              setSwitchToOutlook(false);
            }
          }}
        />
      </Grid>

      <Grid item xs={12} sx={{ overflowY: 'auto', height: 450 }} mb={2}>
        {loader ? (
          <Spinner />
        ) : (
          <>
            {!isEmpty(events) ? (
              events.map((event, index) => (
                <CalendarEventListingCard
                  calendarObj={event}
                  onCopy={onCopy}
                  key={event.eventId}
                  onEdit={() => {
                    setSelectedEventIndex(index);
                    setOpenEdit(true);
                  }}
                />
              ))
            ) : (
              <Grid mt={31} container justifyContent="center">
                <WsTypography>No event scheduled</WsTypography>
              </Grid>
            )}
          </>
        )}
      </Grid>

      <WsDialog
        openDialog={openEventCreation}
        onClose={() => {
          setOpenEventCreation(false);
          setOpenEdit(false);
        }}
      >
        <CalendarEvent
          account={calendarEventAccountGoogle}
          oAccount={calendarEventAccountOutlook}
          onClose={() => {
            setOpenEventCreation(false);
            setOpenEdit(false);
          }}
          defaultValues={openEdit ? events[eventIndex] : undefined}
          updateForOutlook={switchToOutlook}
          onDelete={deleteEvent}
        />
      </WsDialog>
      {events.length > 0 && (
        <CalendarEventCardEditDialog
          open={openEdit}
          calendarObj={events[eventIndex]}
          onCopy={onCopy}
          onClose={() => setOpenEdit(false)}
          onEdit={() => setOpenEventCreation(true)}
          onDelete={() => deleteEvent()}
        />
      )}
    </DashboardGrid>
  );
};

export default CalendarBoard;
